import { useState } from 'react';
import { allMessages, type MerchItem, type MerchVariant, type Message } from '@ce/core';
import { btn, divider, emptyBox, kicker, settle, t } from '../styles';
import { Sheen } from '../components/Ambient';
import type { ScreenProps } from './types';

/** Admission. Buying here issues a credential at checkout. */
export function Tickets({ bundle, state, dispatch }: ScreenProps) {
  const tickets = bundle.tickets ?? [];
  const cur = bundle.event.currency;

  return (
    <div className="stagger">
      <p style={{ fontSize: 12.5, lineHeight: 1.6, color: t(0.55), margin: '0 0 4px' }}>
        {bundle.strings.ticketsIntro}
      </p>

      <div style={{ display: 'grid', gap: 10 }}>
        {tickets.map((ticket, i) => {
          const inCart = state.cart.some(
            (l) => l.kind === 'ticket' && l.ticketId === ticket.id,
          );
          return (
            <div
              key={ticket.id}
              style={{
                position: 'relative', overflow: 'hidden',
                padding: 15, borderRadius: 11,
                background: 'var(--color-surface)', boxShadow: 'var(--shadow-sm)',
                opacity: ticket.soldOut ? 0.5 : 1, ...settle(i),
              }}
            >
              {!ticket.soldOut && i === 0 && <Sheen delay={2} />}
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, position: 'relative' }}>
                <div style={{ flex: 1, font: '500 16px/1.2 var(--font-body)' }}>{ticket.name}</div>
                <div style={{ font: '500 17px/1 var(--font-body)', color: 'var(--color-accent-300)' }}>
                  {cur}{ticket.price}
                </div>
              </div>
              <div style={{ fontSize: 12.5, lineHeight: 1.55, color: t(0.6), marginTop: 7 }}>
                {ticket.desc}
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 11 }}>
                {ticket.perks.map((p) => (
                  <span
                    key={p}
                    style={{
                      fontSize: 10, padding: '3px 9px', borderRadius: 6,
                      background: 'var(--color-neutral-800)', color: 'var(--color-neutral-100)',
                    }}
                  >
                    {p}
                  </span>
                ))}
              </div>
              <button
                type="button"
                disabled={ticket.soldOut}
                onClick={() =>
                  dispatch({
                    type: 'addTicket',
                    line: { kind: 'ticket', ticketId: ticket.id, title: ticket.name, price: ticket.price, qty: 1 },
                  })
                }
                style={btn('primary', {
                  width: '100%', height: 42, marginTop: 13, borderRadius: 9,
                  cursor: ticket.soldOut ? 'not-allowed' : 'pointer',
                })}
              >
                {ticket.soldOut ? 'Sold out' : inCart ? 'Add another' : `Add to cart · ${cur}${ticket.price}`}
              </button>
            </div>
          );
        })}
      </div>
      <div style={{ height: 20 }} />
    </div>
  );
}

/** Merchandise. Ordered here, collected on site. */
export function Shop({ bundle, dispatch }: ScreenProps) {
  const merch = bundle.merch ?? [];
  return (
    <div className="stagger">
      <p style={{ fontSize: 12.5, lineHeight: 1.6, color: t(0.55), margin: '0 0 4px' }}>
        {bundle.strings.merchIntro}
      </p>
      <div style={{ display: 'grid', gap: 10 }}>
        {merch.map((item, i) => (
          <MerchCard key={item.id} item={item} index={i} bundle={bundle} dispatch={dispatch} />
        ))}
      </div>
      <div style={{ height: 20 }} />
    </div>
  );
}

function MerchCard({
  item, index, bundle, dispatch,
}: {
  item: MerchItem; index: number;
  bundle: ScreenProps['bundle']; dispatch: ScreenProps['dispatch'];
}) {
  const variants = item.variants ?? [];
  const [variant, setVariant] = useState<MerchVariant | null>(
    variants.find((v) => !v.soldOut) ?? null,
  );
  const cur = bundle.event.currency;
  // A sized item with nothing picked cannot be added — better to disable the
  // button than to guess a size on someone's behalf.
  const blocked = item.soldOut || (variants.length > 0 && !variant);

  return (
    <div
      style={{
        padding: 15, borderRadius: 11, background: 'var(--color-surface)',
        boxShadow: 'var(--shadow-sm)', opacity: item.soldOut ? 0.5 : 1, ...settle(index),
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
        <div style={{ flex: 1, font: '500 15.5px/1.2 var(--font-body)' }}>{item.name}</div>
        <div style={{ font: '500 16px/1 var(--font-body)' }}>{cur}{item.price}</div>
      </div>
      <div style={{ fontSize: 12.5, lineHeight: 1.55, color: t(0.6), marginTop: 7 }}>
        {item.desc}
      </div>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
        {item.exclusive && (
          <span
            style={{
              fontSize: 10, padding: '3px 9px', borderRadius: 6,
              border: '1px solid var(--color-accent)', color: 'var(--color-accent-300)',
            }}
          >
            Event exclusive
          </span>
        )}
        {item.pickupBooth && (
          <span
            style={{
              fontSize: 10, padding: '3px 9px', borderRadius: 6,
              background: 'var(--color-neutral-800)', color: 'var(--color-neutral-100)',
            }}
          >
            Collect at {item.pickupBooth}
          </span>
        )}
      </div>

      {variants.length > 0 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 12 }}>
          {variants.map((v) => {
            const on = variant?.id === v.id;
            return (
              <button
                key={v.id}
                type="button"
                disabled={v.soldOut}
                onClick={() => setVariant(v)}
                style={{
                  minWidth: 44, padding: '8px 12px', borderRadius: 8,
                  cursor: v.soldOut ? 'not-allowed' : 'pointer',
                  opacity: v.soldOut ? 0.35 : 1,
                  border: `1px solid ${on ? 'var(--color-accent)' : divider}`,
                  background: on ? 'rgba(var(--color-accent-rgb), 0.16)' : 'transparent',
                  color: on ? 'var(--color-accent-300)' : 'var(--color-text)',
                  font: '500 12.5px/1 var(--font-body)',
                }}
              >
                {v.label}
              </button>
            );
          })}
        </div>
      )}

      <button
        type="button"
        disabled={blocked}
        onClick={() =>
          dispatch({
            type: 'addMerch',
            line: {
              kind: 'merch', merchId: item.id, title: item.name, price: item.price, qty: 1,
              variantId: variant?.id, variantLabel: variant?.label,
            },
          })
        }
        style={btn('primary', {
          width: '100%', height: 42, marginTop: 13, borderRadius: 9,
          opacity: blocked ? 0.5 : 1, cursor: blocked ? 'not-allowed' : 'pointer',
        })}
      >
        {item.soldOut ? 'Sold out' : `Add to cart · ${cur}${item.price}`}
      </button>
    </div>
  );
}

const KIND_LABEL: Record<Message['kind'], string> = {
  announcement: 'Announcement',
  schedule: 'Schedule change',
  order: 'Order',
  reminder: 'Reminder',
};

/**
 * The inbox.
 *
 * Push notifications land here as well as on the lock screen, so anything that
 * buzzed while the phone was in a pocket is still readable. A notification
 * someone swiped away should not be gone for good.
 */
export function Messages({ bundle, state, dispatch }: ScreenProps) {
  const messages = allMessages(bundle, state.inbox);

  return (
    <div className="stagger">
      {messages.length === 0 ? (
        <div style={emptyBox}>{bundle.strings.messagesEmpty}</div>
      ) : (
        <div style={{ display: 'grid', gap: 8 }}>
          {messages.map((m, i) => {
            const unread = !state.readMessages.includes(m.id);
            return (
              <button
                key={m.id}
                type="button"
                onClick={() => {
                  dispatch({ type: 'readMessage', id: m.id });
                  if (m.target) dispatch({ type: 'go', screen: m.target.screen });
                }}
                style={{
                  display: 'flex', gap: 11, width: '100%', textAlign: 'left',
                  padding: '13px 14px', borderRadius: 10, cursor: 'pointer',
                  border: `1px solid ${unread ? 'var(--color-accent-700)' : divider}`,
                  background: unread ? 'var(--color-accent-900)' : 'transparent',
                  color: 'var(--color-text)', ...settle(i),
                }}
              >
                <span
                  style={{
                    flex: 'none', width: 7, height: 7, borderRadius: '50%', marginTop: 5,
                    background: unread ? 'var(--color-accent)' : 'transparent',
                    border: unread ? 'none' : `1px solid ${t(0.2)}`,
                  }}
                />
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={kicker(9, t(0.42), 0.14)}>{KIND_LABEL[m.kind]}</span>
                    <span style={{ font: '400 10px/1 var(--font-mono)', color: t(0.35) }}>
                      {new Date(m.at).toLocaleString(undefined, {
                        weekday: 'short', hour: '2-digit', minute: '2-digit',
                      })}
                    </span>
                  </span>
                  <span
                    style={{
                      display: 'block', font: '500 14px/1.25 var(--font-body)', marginTop: 6,
                    }}
                  >
                    {m.title}
                  </span>
                  <span
                    style={{
                      display: 'block', fontSize: 12, lineHeight: 1.5, color: t(0.55), marginTop: 4,
                    }}
                  >
                    {m.body}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      )}

      {messages.some((m) => !state.readMessages.includes(m.id)) && (
        <button
          type="button"
          onClick={() => dispatch({ type: 'readAllMessages', ids: messages.map((m) => m.id) })}
          style={btn('secondary', { width: '100%', height: 42, marginTop: 14 })}
        >
          Mark all as read
        </button>
      )}
      <div style={{ height: 20 }} />
    </div>
  );
}
